import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { sectionShell, sectionHeader, sectionTitle, sectionDescription } from '../../styles/sectionStyles';
import { motion } from 'framer-motion';
import {
  FaBolt,
  FaCalendarAlt,
  FaCopy,
  FaEnvelope,
  FaFacebookF,
  FaGithub,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPen,
  FaPhoneAlt,
  FaQuoteLeft,
  FaShieldAlt,
  FaTwitter,
  FaUser,
} from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { FiDownload } from 'react-icons/fi';
import emailjs from 'emailjs-com';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Bio } from '../../data/constants';
import { validateContact } from '../../utils/contactValidation';

const contactItems = [
  {
    label: 'Email',
    value: Bio.email,
    href: `mailto:${Bio.email}`,
    icon: FaEnvelope,
    color: 'var(--accent)',
    bg: 'var(--orange-bg)',
    copy: Bio.email,
  },
  {
    label: 'Phone',
    value: Bio.phone,
    href: `tel:${Bio.phone.replace(/\s/g, '')}`,
    icon: FaPhoneAlt,
    color: 'var(--green)',
    bg: 'var(--green-bg)',
    copy: Bio.phone,
  },
  {
    label: 'Location',
    value: Bio.location,
    icon: FaMapMarkerAlt,
    color: 'var(--purple)',
    bg: 'var(--purple-bg)',
    copy: Bio.location,
  },
  {
    label: 'Availability',
    value: 'Open to Work',
    icon: FaCalendarAlt,
    color: 'var(--blue)',
    bg: 'var(--blue-bg)',
    copy: 'Open to Work',
  },
];

const socials = [
  { label: 'GitHub', icon: FaGithub, href: Bio.github, color: 'var(--text)' },
  { label: 'LinkedIn', icon: FaLinkedinIn, href: Bio.linkedin, color: '#0a66c2' },
  { label: 'Twitter', icon: FaTwitter, href: Bio.twitter, color: '#1d9bf0' },
  { label: 'Instagram', icon: FaInstagram, href: Bio.insta, color: '#e1306c' },
  { label: 'Facebook', icon: FaFacebookF, href: Bio.facebook, color: '#1877f2' },
];

const trustItems = [
  { title: 'Backend Engineering', desc: 'APIs and application services', icon: FaBolt, color: 'var(--accent)', bg: 'var(--orange-bg)' },
  { title: 'Full-Stack Development', desc: 'Frontend, backend, and databases', icon: FaShieldAlt, color: 'var(--green)', bg: 'var(--green-bg)' },
  { title: 'AI Integration', desc: 'Practical automation workflows', icon: FaQuoteLeft, color: 'var(--purple)', bg: 'var(--purple-bg)' },
];

const Container = styled.section`
  ${sectionShell}
`;

const Wrapper = styled.div`
  width: min(1180px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
`;

const Header = styled.div`
  ${sectionHeader}
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  color: var(--accent);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 12px 28px rgba(72, 50, 28, 0.06);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 5px rgba(249, 115, 22, 0.12);
  }
`;

const Title = styled.h2`
  ${sectionTitle}
`;

const Accent = styled.span`
  color: var(--accent);
`;

const Desc = styled.p`
  ${sectionDescription}
`;

const ContentGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 0.78fr 1.05fr;
  gap: 26px;
  align-items: stretch;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.article`
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
`;

const InfoPanel = styled(Panel)`
  padding: 26px;
  display: flex;
  flex-direction: column;
  gap: 26px;

  @media (max-width: 640px) {
    padding: 20px;
  }
`;

const FormPanel = styled(Panel)`
  padding: 26px 28px;

  @media (max-width: 640px) {
    padding: 20px;
  }
`;

const PanelTitle = styled.h3`
  position: relative;
  margin: 0 0 28px;
  color: var(--text);
  font-family: 'Newsreader', Georgia, serif;
  font-size: 25px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: -0.035em;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -14px;
    width: 42px;
    height: 2px;
    background: var(--accent);
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContactRow = styled.div`
  display: grid;
  grid-template-columns: 54px 1fr 38px;
  align-items: center;
  gap: 14px;
  min-height: 62px;
  padding: 8px 12px 8px 8px;
  border-radius: 15px;
  background: var(--surface);
  border: 1px solid var(--border);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(249, 115, 22, 0.26);
    box-shadow: 0 16px 34px rgba(72, 50, 28, 0.08);
  }
`;

const ContactIcon = styled.div`
  width: 46px;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  color: ${({ $item }) => $item.color};
  background: ${({ $item }) => $item.bg};
  font-size: 18px;
`;

const ContactText = styled.div`
  min-width: 0;
`;

const ContactLabel = styled.div`
  color: var(--text);
  font-size: 14px;
  font-weight: 650;
`;

const ContactValue = styled.div`
  margin-top: 5px;
  color: ${({ $available }) => $available ? 'var(--green)' : 'var(--muted)'};
  font-size: 13px;
  line-height: 1.35;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const CopyButton = styled.button`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--border);
  color: var(--muted);
  background: var(--surface);
  cursor: pointer;
  transition: transform 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: var(--accent);
    border-color: rgba(249, 115, 22, 0.32);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid rgba(249, 115, 22, 0.2);
    outline-offset: 2px;
  }
`;

const SocialTitle = styled(PanelTitle)`
  margin-top: 4px;
  margin-bottom: 26px;
  font-size: 22px;
`;

const SocialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 520px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SocialLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  color: var(--text);
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;

  span {
    width: 52px;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: ${({ $color }) => $color};
    background: var(--surface);
    border: 1px solid var(--border);
    box-shadow: 0 10px 24px rgba(72, 50, 28, 0.06);
    font-size: 20px;
    transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  }

  &:hover span {
    transform: translateY(-3px);
    border-color: rgba(249, 115, 22, 0.28);
    box-shadow: 0 16px 30px rgba(72, 50, 28, 0.1);
  }

  &:focus-visible {
    outline: 3px solid rgba(249, 115, 22, 0.2);
    outline-offset: 4px;
    border-radius: 12px;
  }
`;

const FormStack = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InputShell = styled.div`
  position: relative;
`;

const FieldIcon = styled.span`
  position: absolute;
  left: 17px;
  top: ${({ $textarea }) => $textarea ? '18px' : '50%'};
  transform: ${({ $textarea }) => $textarea ? 'none' : 'translateY(-50%)'};
  color: var(--muted);
  font-size: 15px;
  pointer-events: none;
`;

const fieldBase = `
  width: 100%;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-size: 15px;
  font-weight: 600;
  outline: none;
  box-shadow: inset 0 1px 0 var(--surface);
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;

  &::placeholder {
    color: var(--muted);
    font-weight: 500;
  }

  &:focus {
    border-color: rgba(249, 115, 22, 0.55);
    box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.12);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ContactInput = styled.input`
  ${fieldBase}
  height: 54px;
  padding: 0 16px 0 48px;
`;

const ContactInputMessage = styled.textarea`
  ${fieldBase}
  min-height: 170px;
  resize: vertical;
  padding: 18px 16px 18px 48px;
`;

const SubmitButton = styled.button`
  width: 100%;
  min-height: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: none;
  border-radius: 12px;
  color: var(--button-text);
  background: var(--button-bg);
  font-size: 15px;
  font-weight: 650;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(15, 35, 61, 0.2), 0 0 0 1px rgba(249, 115, 22, 0.36) inset;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
  opacity: ${({ disabled }) => disabled ? 0.62 : 1};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 20px 42px rgba(15, 35, 61, 0.26), 0 0 0 1px rgba(249, 115, 22, 0.5) inset;
    filter: brightness(1.03);
  }

  &:focus-visible {
    outline: 3px solid rgba(249, 115, 22, 0.22);
    outline-offset: 3px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ReplyNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;

  svg {
    color: var(--muted);
  }
`;

const ErrorMessage = styled.div`
  margin: 6px 2px 0;
  color: #ef4444;
  font-size: 12px;
  font-weight: 700;
`;

const FormFeedback = styled.div`
  margin-bottom: 14px;
`;

const BottomBar = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(240px, 1.35fr) repeat(3, minmax(150px, 1fr)) auto;
  gap: 18px;
  align-items: center;
  padding: 20px 22px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);

  @media (max-width: 1050px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    padding: 18px;
  }
`;

const QuoteBox = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 14px;
  align-items: center;
`;

const QuoteIcon = styled.div`
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: var(--accent);
  background: var(--orange-bg);
  font-size: 18px;
`;

const QuoteTitle = styled.div`
  color: var(--text);
  font-size: 14px;
  font-weight: 650;
`;

const QuoteDesc = styled.div`
  margin-top: 6px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
`;

const TrustItem = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 12px;
  align-items: center;
`;

const TrustIcon = styled.div`
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ $item }) => $item.color};
  background: ${({ $item }) => $item.bg};
`;

const TrustTitle = styled.div`
  color: var(--text);
  font-size: 13px;
  font-weight: 650;
`;

const TrustDesc = styled.div`
  margin-top: 4px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
`;

const ResumeButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 10px;
  color: var(--button-text);
  background: var(--button-bg);
  text-decoration: none;
  font-size: 13px;
  font-weight: 650;
  box-shadow: 0 14px 28px rgba(15, 35, 61, 0.2);
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 1050px) {
    justify-self: start;
  }
`;

const FieldLabel = styled.label`
  display: block; margin-bottom: 8px; color: var(--text); font-size: 14px; font-weight: 700;
`;
const Contact = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [copyFeedback, setCopyFeedback] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [feedbackType, setFeedbackType] = useState('success');
  const form = useRef();

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback('Copied to clipboard.');
    } catch (error) {
      setCopyFeedback('Clipboard unavailable. You can select and copy: ' + value);
    }
  };

  const validateForm = () => {
    const formData = new FormData(form.current);
    const newErrors = validateContact(Object.fromEntries(formData.entries()));
    setErrors(newErrors);
    const firstField = Object.keys(newErrors)[0];
    if (firstField) form.current.elements.namedItem(firstField)?.focus();
    return !firstField;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setFeedbackMessage('Please fix the errors above and try again.');
      setFeedbackType('error');
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userId = process.env.REACT_APP_EMAILJS_USER_ID;
    if (!serviceId || !templateId || !userId) {
      setFeedbackMessage(`The contact form is currently unavailable. Please email ${Bio.email} directly.`);
      setFeedbackType('error');
      return;
    }
    ['from_name', 'from_email', 'subject', 'message'].forEach(name => {
      const field = form.current.elements.namedItem(name);
      field.value = field.value.trim();
    });
    setIsLoading(true);
    setFeedbackMessage(null);
    Promise.resolve().then(() => emailjs.sendForm(serviceId, templateId, form.current, userId))
      .then(
        () => {
          setIsLoading(false);
          setOpen(true);
          setFeedbackMessage("Message sent successfully! I'll be in touch soon.");
          setFeedbackType('success');
          form.current.reset();
          setErrors({});
        },
        () => {
          setIsLoading(false);
          setFeedbackMessage(`Failed to send. Please try again or email directly: ${Bio.email}`);
          setFeedbackType('error');
        }
      );
  };

  return (
    <Container id="contact">
      <Wrapper>
        <Header as={motion.div} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <Eyebrow>Get in Touch</Eyebrow>
          <Title>Let's Work <Accent>Together</Accent></Title>
          <Desc>I’m open to software engineering opportunities in backend development, full-stack engineering, and AI integration. If my work fits your team, I’d be glad to talk.</Desc>
        </Header>

        <ContentGrid>
          <InfoPanel as={motion.article} initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.18 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
            <div>
              <PanelTitle>Contact Information</PanelTitle>
              <ContactList>
                {contactItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <ContactRow key={item.label}>
                      <ContactIcon $item={item}><Icon /></ContactIcon>
                      <ContactText>
                        <ContactLabel>{item.label}</ContactLabel>
                        <ContactValue $available={item.label === 'Availability'}>{item.href ? <a href={item.href} style={{ color: 'inherit' }}>{item.value}</a> : item.value}</ContactValue>
                      </ContactText>
                      <CopyButton type="button" aria-label={`Copy ${item.label}`} onClick={() => copyToClipboard(item.copy)}>
                        <FaCopy />
                      </CopyButton>
                    </ContactRow>
                  );
                })}
              </ContactList>
              <p role="status" aria-live="polite" style={{ marginTop: 12, color: 'var(--muted)', fontSize: 13 }}>{copyFeedback}</p>
            </div>

            <div>
              <SocialTitle>Connect With Me</SocialTitle>
              <SocialGrid>
                {socials.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SocialLink key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" $color={item.color} aria-label={item.label}>
                      <span><Icon /></span>
                      {item.label}
                    </SocialLink>
                  );
                })}
              </SocialGrid>
            </div>
          </InfoPanel>

          <FormPanel as={motion.article} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.18 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
            <PanelTitle>Send Me a Message</PanelTitle>

            {feedbackMessage && (
              <FormFeedback role="status" aria-live="polite">
                <Alert severity={feedbackType === 'success' ? 'success' : 'error'}>
                  {feedbackMessage}
                </Alert>
              </FormFeedback>
            )}

            <FormStack ref={form} onSubmit={handleSubmit} noValidate aria-busy={isLoading}>
              <FormGrid>
                <div><FieldLabel htmlFor="contact-from_name">Name</FieldLabel><InputShell>
                  <FieldIcon><FaUser /></FieldIcon>
                  <ContactInput
                    placeholder="Your Name"
                    name="from_name"
                    autoComplete="name"
                  id="contact-from_name"
                  required
                  maxLength={120}
                  aria-describedby={errors.from_name ? 'error-from_name' : undefined}
                    disabled={isLoading}
                    style={{ borderColor: errors.from_name ? '#ef4444' : undefined }}
                    aria-invalid={Boolean(errors.from_name)}
                  />
                  {errors.from_name && <ErrorMessage id="error-from_name">{errors.from_name}</ErrorMessage>}
                </InputShell></div>
                <div><FieldLabel htmlFor="contact-from_email">Email</FieldLabel><InputShell>
                  <FieldIcon><FaEnvelope /></FieldIcon>
                  <ContactInput
                    placeholder="Your Email"
                    name="from_email"
                    autoComplete="email"
                  id="contact-from_email"
                  required
                  maxLength={254}
                  aria-describedby={errors.from_email ? 'error-from_email' : undefined}
                    type="email"
                    disabled={isLoading}
                    style={{ borderColor: errors.from_email ? '#ef4444' : undefined }}
                    aria-invalid={Boolean(errors.from_email)}
                  />
                  {errors.from_email && <ErrorMessage id="error-from_email">{errors.from_email}</ErrorMessage>}
                </InputShell></div>
              </FormGrid>

              <div><FieldLabel htmlFor="contact-subject">Subject</FieldLabel><InputShell>
                <FieldIcon><HiOutlineDocumentText /></FieldIcon>
                <ContactInput
                  placeholder="Role, team, or project"
                  name="subject"
                  id="contact-subject"
                  required
                  maxLength={160}
                  aria-describedby={errors.subject ? 'error-subject' : undefined}
                  disabled={isLoading}
                  style={{ borderColor: errors.subject ? '#ef4444' : undefined }}
                  aria-invalid={Boolean(errors.subject)}
                />
                {errors.subject && <ErrorMessage id="error-subject">{errors.subject}</ErrorMessage>}
              </InputShell></div>

              <div><FieldLabel htmlFor="contact-message">Message</FieldLabel><InputShell>
                <FieldIcon $textarea><FaPen /></FieldIcon>
                <ContactInputMessage
                  placeholder="Tell me about the opportunity or project..."
                  rows="5"
                  name="message"
                  id="contact-message"
                  required
                  maxLength={5000}
                  aria-describedby={errors.message ? 'error-message' : undefined}
                  disabled={isLoading}
                  style={{ borderColor: errors.message ? '#ef4444' : undefined }}
                  aria-invalid={Boolean(errors.message)}
                />
                {errors.message && <ErrorMessage id="error-message">{errors.message}</ErrorMessage>}
              </InputShell></div>

              <SubmitButton type="submit" disabled={isLoading}>
                <FaPaperPlane />
                {isLoading ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </FormStack>

            <ReplyNote>
              <FaShieldAlt />
              <span>You can also contact me directly by email.</span>
            </ReplyNote>
          </FormPanel>
        </ContentGrid>

        <BottomBar as={motion.div} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <QuoteBox>
            <QuoteIcon><FaQuoteLeft /></QuoteIcon>
            <div>
              <QuoteTitle>Have a role or project in mind?</QuoteTitle>
              <QuoteDesc>Tell me about your team, the work, and how I could contribute.</QuoteDesc>
            </div>
          </QuoteBox>
          {trustItems.map((item) => {
            const Icon = item.icon;
            return (
              <TrustItem key={item.title}>
                <TrustIcon $item={item}><Icon /></TrustIcon>
                <div>
                  <TrustTitle>{item.title}</TrustTitle>
                  <TrustDesc>{item.desc}</TrustDesc>
                </div>
              </TrustItem>
            );
          })}
          <ResumeButton href={Bio.resume} download>
            <FiDownload />
            Download Resume
          </ResumeButton>
        </BottomBar>

        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          message="Email sent successfully!"
        />
      </Wrapper>
    </Container>
  );
};

export default Contact;
